import { Box, BoxProps } from '@chakra-ui/react'
import * as React from 'react'

import { LayoutContext, Page, isPageType, currentPagePath } from '../context/LayoutContext'
import MainCanvasSuspense from '../components/canvas/MainCanvasSuspense'
import Nav from '../components/nav/Nav'
import Overlay from '../components/overlay/Overlay'
import SEO from '../components/common/SEO'

export type LayoutContentProps = BoxProps & {
  children: React.ReactNode
}

const LayoutContent: React.FC<LayoutContentProps> = ({ children }) => {
  const { layoutState, dispatchLayoutAction } = React.useContext(LayoutContext)
  const [currentPage, setCurrentPage] = React.useState<Page | null>(null)
  const [nextPage, setNextPage] = React.useState<Page | null>(null)

  React.useEffect(() => {
    if (layoutState.initializeFinished && nextPage) {
      setCurrentPage(nextPage)
      setNextPage(null)
      dispatchLayoutAction({
        type: 'startTransition',
        value: { from: null, to: nextPage.path, state: 'pageWillAppear' },
      })
    }
  }, [layoutState.initializeFinished])

  // nextPage 更新
  React.useEffect(() => {
    const path = currentPagePath()
    if (isPageType(path)) {
      if (currentPage?.path !== path || layoutState.transitionType !== null) {
        setNextPage({ path: path, content: children })
      }
    }
  }, [children])

  // ルーターが更新されたら
  React.useEffect(() => {
    if (nextPage) {
      // startTransition(nextPage)
      const prevTransition = layoutState.transitionType
      if (prevTransition === null) {
        // 遷移中でない場合
        if (currentPage) {
          // 最初以外の表示
          dispatchLayoutAction({
            type: 'startTransition',
            value: { from: currentPage.path, to: nextPage.path, state: 'pageWillDisappear' },
          })
        }
      } else {
        // 遷移中だったら
        if (currentPage && prevTransition.state === 'pageWillAppear') {
          // 次のページ表示中だったら
          dispatchLayoutAction({
            type: 'startTransition',
            value: { from: currentPage.path, to: nextPage.path, state: 'pageWillDisappear' },
          })
        } else {
          // to だけ変わる
          dispatchLayoutAction({ type: 'changeTransitionTo', value: nextPage.path })
        }
      }
    }
  }, [nextPage])

  React.useEffect(() => {
    const transition = layoutState.transitionType
    if (transition) {
      switch (transition.state) {
        case 'pageDidDisappear':
          // ページの非表示完了
          setCurrentPage(nextPage)
          setNextPage(null)
          dispatchLayoutAction({ type: 'updateTransition', value: 'pageWillAppear' })
          break
        case 'pageDidAppear':
          // 遷移終了
          dispatchLayoutAction({ type: 'clearTransition' })
          break
        default:
          break
      }
    }
  }, [layoutState.transitionType])

  React.useEffect(() => {
    dispatchLayoutAction({ type: 'finishInitialize' })
  }, [])

  return (
    <>
      <SEO />
      <Box pos="relative" w="100%" h="100%">
        <Nav zIndex="100" />
        <MainCanvasSuspense zIndex="0" />
        <Overlay zIndex="90" />
        <Box pos="absolute" top="0" left="0" w="100%" zIndex="10">
          {currentPage && currentPage.content}
        </Box>
      </Box>
    </>
  )
}

export default LayoutContent
